import { Form, message } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OTPInput } from '../../../../components/OTPInput/OTPInput';
import DispexButton from '../../../../components/DispexButton/DispexButton';
import styles from './OTPForm.module.scss';
import cn from 'classnames';
import { getVerificationCode } from '../../requests/getVerificationCode';
import { useDispatch } from 'react-redux';
import { userActions } from '../../../../actions/user.actions';

const SMS_CODE_COULDOWN = 60;

const OTPNode = ({
  form,
  setStep,
  verificationType,
  username,
  password,
  onFailedAttempt,
  failedAttempts = 0,
  trackAttempts = false,
  supportContactNotice = null,
  deepLink = null,
  isMaxBound = null,
  onVerificationCodeResponse,
  onMaxLinkClick,
}) => {
  const [isComplete, setIsComplete] = useState(false);
  const { resetFields, validateFieldsAndScroll, getFieldDecorator } = form;
  const [seconds, setSeconds] = useState(SMS_CODE_COULDOWN);
  const [attemptCounter, setAttemptCounter] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const isMountedRef = useRef(true);
  const dispatch = useDispatch();
  const MAX_ATTEMPTS = 3;

  const verificationTypeId = verificationType?.id || null;
  const verificationTypeName = verificationType?.name;
  const verificationTypeDescription =
    verificationType?.description || verificationTypeName;
  const isMaxVerification = Number(verificationTypeId) === 5;
  const shouldShowMaxLink = isMaxVerification && isMaxBound === false;
  const shouldShowOtpInput = !isMaxVerification || isMaxBound === true;

  const goToMethodSelect = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }
    resetFields();
    setSeconds(SMS_CODE_COULDOWN);
    if (typeof setStep === 'function') {
      setStep(2);
    }
  }, [resetFields, setStep]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!shouldShowOtpInput) {
      return;
    }

    validateFieldsAndScroll(async (err, values) => {
      if (!err && !isBlocked) {
        let shouldReturnToMethodSelect = false;
        const result = await dispatch(
          userActions.authenticateWithCheckVerificationCode(
            username,
            password,
            values.otp
          )
        );

        if (!isMountedRef.current) {
          return;
        }

        if (result && !result.success) {
          if (trackAttempts) {
            const nextCounter = Math.min(
              MAX_ATTEMPTS,
              (failedAttempts || 0) + 1
            );
            setAttemptCounter(nextCounter);

            if (nextCounter >= MAX_ATTEMPTS) {
              setIsBlocked(true);
              setSeconds(0);
              message.warning(
                'Превышено количество попыток. Выберите другой метод верификации.'
              );
              shouldReturnToMethodSelect = true;
            } else {
              const remainingAttempts = MAX_ATTEMPTS - nextCounter;
              message.warning(
                `Неверный код. Осталось попыток: ${remainingAttempts}. После 3 неверных попыток потребуется запросить новый код.`
              );
            }
          } else if (result.counter !== undefined) {
            setAttemptCounter(result.counter);

            if (result.counter >= MAX_ATTEMPTS) {
              setIsBlocked(true);
              setSeconds(0);
              message.warning(
                'Превышено количество попыток. Выберите другой метод верификации.'
              );
              shouldReturnToMethodSelect = true;
            } else {
              const remainingAttempts = MAX_ATTEMPTS - result.counter;
              message.warning(
                `Неверный код. Осталось попыток: ${remainingAttempts}. После 3 неверных попыток потребуется запросить новый код.`
              );
            }
          }

          if (typeof onFailedAttempt === 'function') {
            onFailedAttempt(verificationType);
          }

          if (shouldReturnToMethodSelect) {
            goToMethodSelect();
            return;
          }
        } else if (result && result.success) {
          setAttemptCounter(null);
          setIsBlocked(false);
        }

        resetFields();
      }
    });
  };

  useEffect(() => {
    if (isBlocked || seconds <= 0) {
      return undefined;
    }
    const interval = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [seconds, isBlocked]);

  useEffect(() => {
    if (trackAttempts && failedAttempts >= MAX_ATTEMPTS) {
      setIsBlocked(true);
      setAttemptCounter(MAX_ATTEMPTS);
      setSeconds(0);
      goToMethodSelect();
    }
  }, [
    trackAttempts,
    failedAttempts,
    verificationTypeId,
    MAX_ATTEMPTS,
    goToMethodSelect,
  ]);

  const resetRetryTimer = () => {
    message.success('Код отправлен повторно');
    setSeconds(SMS_CODE_COULDOWN);
    setAttemptCounter(null);
    setIsBlocked(false);
  };

  const handleRetryCode = async () => {
    if (isBlocked) {
      return;
    }
    const verificationCodeResponse = await getVerificationCode({
      authParameter: {
        login: username,
        password: password,
      },
      verificationTypeId: verificationTypeId,
    });

    if (!isMountedRef.current) {
      return;
    }

    if (!verificationCodeResponse) {
      return;
    }

    if (typeof onVerificationCodeResponse === 'function') {
      const isHandled = await onVerificationCodeResponse(
        verificationType,
        verificationCodeResponse
      );

      if (!isMountedRef.current) {
        return;
      }

      if (isHandled && isMaxVerification) {
        resetRetryTimer();
        return;
      }
    }

    resetRetryTimer();
  };

  return (
    <Form
      onSubmit={handleSubmit}
      className={styles.root}
    >
      <p className="login_page-main-title">{verificationTypeName}</p>
      {shouldShowOtpInput && (
        <Form.Item className={styles.field}>
          {getFieldDecorator('otp', {
            rules: [{ required: true, message: 'Введите код' }],
          })(
            <OTPInput
              length={4}
              setIsComplete={setIsComplete}
              disabled={isBlocked}
            />
          )}
        </Form.Item>
      )}
      {shouldShowOtpInput && (
        <div className={styles.text}>{verificationTypeDescription}</div>
      )}
      {shouldShowMaxLink && (
        <a
          className={cn('login_page-main-entry_button', styles.maxLink)}
          href={deepLink || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            if (!deepLink) {
              event.preventDefault();
              return;
            }

            if (typeof onMaxLinkClick === 'function') {
              setTimeout(onMaxLinkClick, 0);
            }
          }}
        >
          Перейти в МАХ
        </a>
      )}
      {attemptCounter !== null && attemptCounter < MAX_ATTEMPTS && (
        <div className={styles.warning}>
          <span>
            Осталось попыток: {MAX_ATTEMPTS - attemptCounter}. После 3 неверных
            попыток потребуется запросить новый код.
          </span>
        </div>
      )}
      {isBlocked && (
        <div className={styles.error}>
          <span style={{ color: '#ff4d4f' }}>
            Превышено количество попыток. Повторный запрос кода для этого метода
            недоступен. Выберите другой способ подтверждения.
          </span>
          {supportContactNotice && (
            <div className={styles.supportNotice}>{supportContactNotice}</div>
          )}
        </div>
      )}
      <div className={styles.retry}>
        {shouldShowOtpInput && !isBlocked && !!seconds && (
          <span className={styles.retryText}>
            Запросить код повторно через: {seconds} секунд
          </span>
        )}
      </div>
      <div className={styles.actions}>
        {shouldShowOtpInput && (
          <div className={styles.btnsContainer}>
            <DispexButton
              onClick={handleRetryCode}
              type="secondary"
              htmlType="button"
              className={cn(
                'login_page-main-entry_button',
                styles.secondaryBtn
              )}
              disabled={isBlocked || seconds > 0}
            >
              Запросить код повторно
            </DispexButton>
          </div>
        )}
        <div className={styles.btnsContainer}>
          <DispexButton
            onClick={() => {
              goToMethodSelect();
            }}
            type="secondary"
            htmlType="button"
            className={cn('login_page-main-entry_button', styles.secondaryBtn)}
          >
            Назад
          </DispexButton>
          {shouldShowOtpInput && (
            <DispexButton
              htmlType="submit"
              type="primary"
              className="login_page-main-entry_button"
              disabled={!isComplete || isBlocked}
            >
              Войти
            </DispexButton>
          )}
        </div>
      </div>
    </Form>
  );
};

export const OTPForm = Form.create({ name: 'otp_form' })(OTPNode);
